import { Icon, IconButton, Tooltip, Box, useMediaQuery, Menu, MenuList, MenuButton, MenuItem } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import { IconThunder, IconTrashbin, ICMoreDots } from 'assets/icons';
import { getTasksCommonColumns } from 'containers/JobsTasks/shared/taskCommonColumns';

interface ITaskTableColumns {
  handleSingleActivate: (id: number) => void;
  handleSingleDelete: (id: number) => void;
}

export const useTableColumns = ({ handleSingleActivate, handleSingleDelete }: ITaskTableColumns) => {
  const { t } = useTranslation(['common', 'jobsTasks']);

  const [isSmallerThan768, isLargerThan1280] = useMediaQuery(['(max-width: 767px)', '(min-width: 1280px)'], {
    ssr: false,
  });

  const columns = useMemo(() => {
    const commonColumns = getTasksCommonColumns(t);

    return [
      ...commonColumns,
      {
        Header: '',
        accessor: 'action',
        width: isLargerThan1280 || isSmallerThan768 ? 200 : 75,
        Cell: (data: CellValue) => {
          return (
            <Box>
              {isLargerThan1280 || isSmallerThan768 ? (
                <>
                  <Tooltip label={t('jobsTasks:activateTask')} hasArrow bg="black">
                    <IconButton
                      aria-label={t('jobsTasks:activateTask')}
                      bg="rgba(101, 104, 134, 0.1)"
                      icon={<Icon as={IconThunder} />}
                      size="sm"
                      m="1"
                      _hover={{ bg: 'rgba(101, 104, 134, 0.2)' }}
                      onClick={() => handleSingleActivate(data.row.original.id)}
                      data-testid={`activate-task-${data.row.original.id}`}
                    />
                  </Tooltip>
                  <Tooltip label={t('jobsTasks:deleteTask')} hasArrow bg="black">
                    <IconButton
                      aria-label="Delete"
                      bg="rgba(255, 83, 83, 0.1)"
                      icon={<Icon as={IconTrashbin} />}
                      m="1"
                      size="sm"
                      _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                      onClick={() => handleSingleDelete(data.row.original.id)}
                      data-testid={`delete-task-${data.row.original.id}`}
                    />
                  </Tooltip>
                </>
              ) : (
                <Menu strategy="fixed" placement="left">
                  <MenuButton
                    as={IconButton}
                    colorScheme="whiteAlpha"
                    aria-label="Options"
                    icon={<Icon as={ICMoreDots} boxSize="8" />}
                    variant="outline"
                  />
                  <MenuList>
                    <MenuItem justifyContent="space-between" onClick={() => handleSingleActivate(data.row.original.id)}>
                      {t('jobsTasks:activateTask')}
                      <IconButton
                        as="span"
                        aria-label={t('jobsTasks:activateTask')}
                        bg="rgba(101, 104, 134, 0.1)"
                        icon={<Icon as={IconThunder} />}
                        size="sm"
                        m="1"
                        _hover={{ bg: 'rgba(101, 104, 134, 0.2)' }}
                        data-testid={`activate-task-${data.row.original.id}`}
                      />
                    </MenuItem>
                    <MenuItem justifyContent="space-between" onClick={() => handleSingleDelete(data.row.original.id)}>
                      {t('jobsTasks:deleteTask')}
                      <IconButton
                        as="span"
                        aria-label={t('jobsTasks:deleteTask')}
                        bg="rgba(255, 83, 83, 0.1)"
                        icon={<Icon as={IconTrashbin} />}
                        m="1"
                        size="sm"
                        _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                        onClick={() => handleSingleDelete(data.row.original.id)}
                        data-testid={`delete-task-${data.row.original.id}`}
                      />
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
            </Box>
          );
        },
      },
    ];

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSingleActivate, handleSingleDelete]);

  return { columns };
};
